import React, { useState, useEffect } from 'react';
import { useNotifications } from '../services/notifications';
import { useAuth } from '../hooks/useAuth';
import { useNotificationCompatibility } from '../hooks/useNotificationCompatibility';

interface NotificationBannerProps {
  position?: 'top' | 'bottom' | 'profile';
  showAlways?: boolean;
  onPermissionGranted?: () => void;
  onPermissionDenied?: () => void;
}

const NotificationBanner: React.FC<NotificationBannerProps> = ({
  position = 'top',
  showAlways = false,
  onPermissionGranted,
  onPermissionDenied
}) => {
  const { requestPermission, getPermissionStatus } = useNotifications();
  const { isWaiter, user } = useAuth();
  const { isSupported } = useNotificationCompatibility();
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission>('default');
  const [isRequesting, setIsRequesting] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showReconfigure, setShowReconfigure] = useState(false);

  useEffect(() => {
    const status = getPermissionStatus();
    setPermissionStatus(status);
    
    // Mostrar banner si no hay permisos y showAlways es true, o si no hay permisos
    if ((showAlways && status === 'default') || status === 'default') {
      setShowBanner(true);
    }
  }, [getPermissionStatus, showAlways]);

  const handleRequestPermission = async () => {
    setIsRequesting(true);
    
    try {
      // Pasar el userId del usuario actual
      const userId = user?.uid || null;
      if (!userId) {
        console.warn('⚠️ No se pudo obtener ID de usuario para notificaciones');
        setPermissionStatus('denied');
        onPermissionDenied?.();
        showErrorMessage();
        return;
      }
      
      const granted = await requestPermission(userId);
      
      if (granted) {
        setPermissionStatus('granted');
        setShowBanner(false);
        setShowReconfigure(false);
        onPermissionGranted?.();
        
        // Mostrar mensaje de éxito
        showSuccessMessage();
      } else {
        setPermissionStatus('denied');
        onPermissionDenied?.();
        
        // Mostrar mensaje de error
        showErrorMessage();
      }
    } catch (error) {
      console.error('Error solicitando permisos:', error);
      setPermissionStatus('denied');
      onPermissionDenied?.();
      showErrorMessage();
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowReconfigure(false);
    // Recordar que se descartó por 24 horas
    localStorage.setItem('notification-banner-dismissed', Date.now().toString());
  };

  // Definir las clases de posicionamiento responsive
  const getPositionClasses = () => {
    const baseClasses = 'fixed z-40 mx-4';
    
    switch (position) {
      case 'top':
        return `${baseClasses} top-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm`;
      case 'profile':
        return `${baseClasses} bottom-32 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm`;
      case 'bottom':
        return `${baseClasses} bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm`;
      default:
        return `${baseClasses} top-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm`;
    }
  };

  const showSuccessMessage = () => {
    // Crear un toast de éxito responsive
    const toast = document.createElement('div');
    toast.className = 'fixed top-4 left-4 right-4 sm:left-auto sm:right-4 bg-green-500 text-white px-4 py-3 sm:px-6 sm:py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full sm:translate-x-full max-w-sm mx-auto sm:mx-0';
    toast.innerHTML = `
      <div class="flex items-center space-x-2 text-sm sm:text-base">
        <span class="text-lg sm:text-xl">✅</span>
        <span class="flex-1">¡Notificaciones de mesero habilitadas!</span>
      </div>
    `;
    
    document.body.appendChild(toast);
    
    // Animar entrada
    setTimeout(() => {
      toast.classList.remove('translate-x-full');
    }, 100);
    
    // Animar salida y remover
    setTimeout(() => {
      toast.classList.add('translate-x-full');
      setTimeout(() => {
        if (document.body.contains(toast)) {
          document.body.removeChild(toast);
        }
      }, 300);
    }, 3000);
  };

  const showErrorMessage = () => {
    // Crear un toast de error responsive
    const toast = document.createElement('div');
    toast.className = 'fixed top-4 left-4 right-4 sm:left-auto sm:right-4 bg-red-500 text-white px-4 py-3 sm:px-6 sm:py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full sm:translate-x-full max-w-sm mx-auto sm:mx-0';
    toast.innerHTML = `
      <div class="flex items-center space-x-2 text-sm sm:text-base">
        <span class="text-lg sm:text-xl">❌</span>
        <span class="flex-1">Permisos de notificación denegados</span>
      </div>
    `;
    
    document.body.appendChild(toast);
    
    // Animar entrada
    setTimeout(() => {
      toast.classList.remove('translate-x-full');
    }, 100);
    
    // Animar salida y remover
    setTimeout(() => {
      toast.classList.add('translate-x-full');
      setTimeout(() => {
        if (document.body.contains(toast)) {
          document.body.removeChild(toast);
        }
      }, 300);
    }, 4000);
  };

  // Solo mostrar notificaciones para waiters
  if (!isWaiter()) {
    return null;
  }

  // Solo mostrar si el navegador soporta notificaciones
  if (!isSupported) {
    return null;
  }

  // Si ya hay permisos y no se está reconfigurando, no mostrar nada
  if (permissionStatus === 'granted' && !showAlways && !showReconfigure) {
    return null;
  }

  // Si los permisos fueron denegados y no es showAlways, no mostrar nada
  if (permissionStatus === 'denied' && !showAlways) {
    return null;
  }

  // Si no se debe mostrar el banner y no se está reconfigurando, no mostrar nada
  if (!showBanner && permissionStatus !== 'default' && !showReconfigure) {
    return null;
  }

  // Verificar si se descartó recientemente
  const dismissedTime = localStorage.getItem('notification-banner-dismissed');
  if (dismissedTime) {
    const hoursSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60);
    if (hoursSinceDismissed < 24) {
      return null;
    }
  }

  return (
    <div className={getPositionClasses()}>
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 sm:p-5 transform transition-all duration-300 hover:scale-105">
        <div className="flex items-start space-x-3">
          {/* Icono de notificación */}
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <span className="text-white text-lg">🔔</span>
            </div>
          </div>

          {/* Contenido */}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">
              Notificaciones para Meseros
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 mb-3">
              Recibe notificaciones en tiempo real sobre nuevas órdenes, cambios de estado y solicitudes de clientes
            </p>
            
            {/* Botones de acción */}
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              <button
                onClick={handleRequestPermission}
                disabled={isRequesting}
                className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-xs sm:text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-200 hover:scale-105"
              >
                {isRequesting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-3 w-3 sm:h-4 sm:w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="hidden sm:inline">Configurando...</span>
                    <span className="sm:hidden">Configurando</span>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">Activar Notificaciones de Mesero</span>
                    <span className="sm:hidden">Activar</span>
                  </>
                )}
              </button>
              
              <button
                onClick={handleDismiss}
                className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-xs sm:text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transform transition-all duration-200 hover:scale-105"
              >
                <span className="hidden sm:inline">Más tarde</span>
                <span className="sm:hidden">Cerrar</span>
              </button>
            </div>
          </div>

          {/* Botón de cerrar */}
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 text-gray-400 hover:text-gray-500 transform transition-colors duration-200 p-1 hover:bg-gray-100 rounded-full"
            aria-label="Cerrar notificación"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Estado de permisos */}
        {permissionStatus === 'denied' && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-xs sm:text-sm text-red-600">
              ⚠️ Los permisos de notificación de mesero fueron denegados. 
              Puedes habilitarlos manualmente en la configuración del navegador.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationBanner;
